import { isEqual } from 'lodash';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import ProductsSearchBar from '@/shared/components/widgets/ProductsSearchBar/ProductsSearchBar';
import useProductsSearchBar from '@/shared/components/widgets/ProductsSearchBar/hooks/useProductsSearchBar';
import { AppPath } from '@/shared/constants/path';
import ProductVariant from '@/shared/types/products/productVariant.type';

import ProductItem from './components/ProductItem';

type ProductsSearchPanelProps = {
  currency: string;
  selectedProducts: ProductVariant[];
  onAddProductToSelection: (product: ProductVariant) => void;
  onRemoveSelectedProduct: (id: string) => void;
  onUpdateSelectedProducts: (product: ProductVariant) => void;
};

const ProductsSearchPanel: FC<ProductsSearchPanelProps> = ({
  currency,
  selectedProducts,
  onAddProductToSelection,
  onRemoveSelectedProduct,
  onUpdateSelectedProducts,
}) => {
  const { t } = useTranslation(['orders']);

  const productsSearchBarState = useProductsSearchBar();
  const selectedValueRef = useRef<ProductVariant | null>(null);

  useEffect(() => {
    if (
      !isEqual(productsSearchBarState.selectedValue, selectedValueRef.current)
    ) {
      if (productsSearchBarState.selectedValue) {
        selectedValueRef.current = productsSearchBarState.selectedValue;

        onAddProductToSelection(productsSearchBarState.selectedValue);
      }
    }
  }, [onAddProductToSelection, productsSearchBarState.selectedValue]);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant='body1' sx={{ mb: 1 }}>
        {t('orders:products')}
      </Typography>

      <Stack
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <ProductsSearchBar
          productsSearchBarState={productsSearchBarState}
          externalFields={{ currency }}
        />

        <Button
          LinkComponent={Link}
          size='small'
          href={`${AppPath.Products}/add`}
          target='_blank'
        >
          {t('orders:createProduct')}
        </Button>
      </Stack>

      {selectedProducts.length > 0 && (
        <Stack
          spacing={2}
          sx={{
            overflowY: 'auto',
            py: 3,
          }}
        >
          {selectedProducts.map((product) => (
            <ProductItem
              key={product.variant_id}
              currency={currency}
              product={product}
              onRemoveSelectedProduct={onRemoveSelectedProduct}
              onUpdateSelectedProducts={onUpdateSelectedProducts}
            />
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default ProductsSearchPanel;
